import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { Platform } from 'react-native';

import * as entra from '../auth/entraAuth';
import * as google from '../auth/googleSignIn';
import * as native from '../auth/nativeAuth';
import type { AuthMode, SignupAttributes } from '../auth/nativeAuth.types';
import { registerWebPush, resetWebPushRegistration } from '../push/webpush';
import { oublierEtatPartie } from '../api/missionRun';
import { traceAuth } from '../auth/authTrace';
import { resolveApiBase } from '../config/env';
import { getPendingProfile, setPendingProfile, clearPendingProfile } from '../auth/pendingProfile';
import { apiFetch, setUnauthorizedHandler, setRefreshHandler, setCurrentTokenReader } from '../api/client';
import { clearInsightsUser, setInsightsUser } from '../insights/appInsights';
import { newSessionId } from '../insights/session';
import { onForeground } from '../utils/onForeground';

function currentPath(): string {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    return window.location.pathname + window.location.search;
  }
  return '/';
}

// The app's user profile. The SHAPE is unchanged -- consuming screens are untouched -- but
// since CCT-319 it is filled from `GET /me/profile`, no longer from the token's claims.
export interface AuthUser {
  id: string;
  prenom: string;
  nom: string;
  email: string;
  phone: string;
}

interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  /** Starts the Entra login (PKCE). signup=true -> sign-up. returnTo = the page to restore
   *  after authentication (defaults to the current page). */
  login: (signup?: boolean, returnTo?: string) => Promise<void>;
  /** Sign-in through a federated third party (Google) in a browser flow (native Custom Tab /
   *  web redirect). Web: redirects, so it never resolves. Native: opens the Custom Tab, then
   *  the session. */
  loginWithGoogle: () => Promise<void>;
  /** Native auth (web): starts the flow. mode='signin' -> an OTP was sent;
   *  mode='signup' -> unknown email, the app must collect the attributes then call startSignup. */
  startEmailOtp: (email: string) => Promise<{ mode: AuthMode; target?: string }>;
  /** Native auth (web): sign-up with the mandatory attributes -> sends the OTP. */
  startSignup: (email: string, attrs: SignupAttributes) => Promise<{ target?: string }>;
  /** Native auth (web): validates the OTP and opens the session. On sign-up, `profile` is
   *  supplied -> the profile is created/updated in the database (POST /me/profile). */
  submitOtp: (code: string, profile?: SignupAttributes) => Promise<void>;
  logout: () => void;
  /** ⚠️ WHY THE BOOTSTRAP OPENED NO SESSION, when it is not a plain "not signed in". The
   *  bootstrap swallowed everything into a `console.warn`: a network cut, a cancellation and a
   *  refusal produced the SAME silent screen. Measured on an `ERR_NETWORK_CHANGED` — the user
   *  hunted for a Google account problem while the server answered 200. `null` = nothing to
   *  report. */
  authNotice: string | null;
  /** The current access token (to call /me, /me/stories...). */
  getAccessToken: () => string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const tokenRef = useRef<string | null>(null);

  // Profile reconciliation: replays POST /me/profile from the PENDING profile (the sign-up
  // attributes, stored durably) until the server confirms. On failure the pending profile is
  // KEPT -> retried next session (re-login / app restart). Idempotent server-side (upsert).
  const reconcileProfile = async (token: string | null): Promise<void> => {
    if (!token) return;
    const pending = await getPendingProfile();
    if (!pending) return;
    try {
      const res = await fetch(`${resolveApiBase()}/me/profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          firstName: pending.givenName || null,
          lastName: pending.surname || null,
          country: pending.country || null,
          age: parseInt(pending.age, 10) || null,
        }),
      });
      if (res.ok) {
        await clearPendingProfile(); // confirmed in the database -> stop replaying
      } else {
        // eslint-disable-next-line no-console
        console.warn(`Profile not saved (HTTP ${res.status}) — will retry next session.`);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Profile not saved (network) — will retry next session:', e);
    }
  };

  /** What `GET /me/profile` answers. `userId` is the identity the SERVER states. */
  interface MeProfile {
    userId?: string;
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    phone?: string | null;
  }

  // ⚠️ THE IDENTITY IS READ FROM THE SERVER, NOT DECODED FROM THE TOKEN — CCT-319.
  //
  // This used to be `claimsToUser(s.claims)`: the browser opened the access token and read
  // `oid` / `given_name` / `email` out of it. That has to stop before the token becomes
  // unreadable to JS, and it was worth stopping on its own merits — the claims are FROZEN at
  // sign-in, so a user who corrected their name kept seeing the old one until the token
  // expired, and the profile existed twice (in the token and in the database).
  //
  // A synchronous mirror of `user` is kept: several call sites need to know whether the
  // identity is already loaded WITHOUT waiting for a re-render.
  const userRef = useRef<AuthUser | null>(null);
  const identityInFlight = useRef<Promise<void> | null>(null);

  const rememberUser = (u: AuthUser) => {
    userRef.current = u;
    // IDEMPOTENT (CCT-193): calling `setUser` with a BRAND-NEW object every time changed the
    // `user` reference, which re-armed the renewal effect (dependency [user]), which called
    // `applySession` again... a loop running at network speed -- MEASURED at ~7 req/s, i.e.
    // ~2,460 req/min: PostgreSQL saturated, a climb to 10 replicas, and a flood of telemetry.
    // A new state is only pushed when the identity has REALLY changed.
    setUser((prev) => (prev && prev.id === u.id && prev.email === u.email ? prev : u));
  };

  /** Loads the identity ONCE per session. Resolves when `user` is usable. */
  const loadIdentity = (token: string): Promise<void> => {
    // ⚠️ NOT ON EVERY TOKEN RENEWAL. `applySession` runs again at each silent refresh; asking
    // the server for the identity every time would add a request per renewal AND re-enter the
    // very loop CCT-193 closed. The identity does not change while a session lives.
    if (userRef.current) {
      const u = userRef.current;
      void registerWebPush(token, { userKey: u.email || u.id });
      return Promise.resolve();
    }
    // Concurrent callers (bootstrap + a 401 retry) must share ONE request, never race.
    if (identityInFlight.current) return identityInFlight.current;

    const p = (async () => {
      try {
        const me = await apiFetch<MeProfile>('/me/profile', { token, auth: true, background: true });
        const u: AuthUser = {
          id: me.userId || me.email || 'unknown',
          prenom: me.firstName || '',
          nom: me.lastName || '',
          email: me.email || '',
          phone: me.phone || '',
        };
        rememberUser(u);
        // App Insights (CCT-193, web): attaches the ANONYMOUS session created at bootstrap to
        // the authenticated user. A no-op on native, and when the SDK is inactive.
        setInsightsUser(u.email, u.id);
        // Web Push: registers the service worker and THIS browser's subscription (no-op on
        // native). `registerWebPush` short-circuits once it has already declared THIS
        // subscription for THIS account (CCT-259) -- hence the identity passed as its key.
        void registerWebPush(token, { userKey: u.email || u.id });
      } catch (e) {
        // ⚠️ WE DO NOT SIGN THE USER OUT. The token is valid -- the 401 interceptor has
        // already settled that; it is the profile that could not be read. Clearing the session
        // here would eject an authenticated user over a failed read.
        // eslint-disable-next-line no-console
        console.warn('Identity not loaded — staying signed in:', e);
      } finally {
        identityInFlight.current = null;
      }
    })();
    identityInFlight.current = p;
    return p;
  };

  const applySession = (s: { accessToken: string } | null): Promise<void> => {
    if (!s) return Promise.resolve();
    tokenRef.current = s.accessToken;
    // Also remembered in the auth module: on NATIVE the storage is asynchronous, and the API
    // client (which compares tokens on a 401) needs a SYNCHRONOUS read (CCT-127).
    entra.rememberAccessToken?.(s.accessToken);
    traceAuth('session', s.accessToken);
    // ⚠️ THE PENDING PROFILE IS WRITTEN FIRST, AND AWAITED. It carries the sign-up attributes
    // (first name, last name, country, age). Reading the identity before that POST landed would
    // show a freshly signed-up player an empty name until the next reload.
    return reconcileProfile(s.accessToken).then(() => loadIdentity(s.accessToken));
  };

  const [authNotice, setAuthNotice] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        // 0) Back from the Google redirect (web): /auth/google/callback.
        const fromGoogle = await google.completeGoogleIfCallback();
        if (fromGoogle) {
          await applySession(fromGoogle);
          setIsLoading(false);
          return;
        }
        // 1) Back from the Entra redirect (web).
        const fromCallback = await entra.completeLoginIfCallback();
        if (fromCallback) {
          await applySession(fromCallback);
          // returnTo: restore the page aimed at before authentication.
          const rt = entra.consumeReturnTo();
          if (rt && Platform.OS === 'web' && typeof window !== 'undefined' && rt !== currentPath()) {
            window.location.replace(rt);
            return;
          }
        } else {
          // 2) otherwise, the stored session.
          await applySession(await entra.restoreSession());
        }
      } catch (e) {
        // Auth unavailable or cancelled -> stay signed out, but SAY WHY when we know.
        const kind = (e as { kind?: string } | null)?.kind;
        const reseau = kind === 'network'
          || (e instanceof TypeError && /fetch/i.test(e.message || ''));
        setAuthNotice(reseau ? 'network' : null);
        // eslint-disable-next-line no-console
        console.warn('Auth bootstrap:', e);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  // ONE SINGLE WAY TO SIGN OUT — CCT-345. The renewal tick must be able to sign out a user
  // whose coupon is dead, and it must do it EXACTLY as the 401 interceptor does: remember the
  // page, clear the synchronous mirror, leave the private screen. Two copies of that gesture
  // would drift apart, and the symptom would be a sign-out that only half signs out.
  const deconnecter = React.useCallback((motif?: string) => {
    traceAuth('logout', tokenRef.current, motif);
    entra.rememberReturnTo(currentPath());
    tokenRef.current = null;
    userRef.current = null;   // ⚠️ THE SYNCHRONOUS MIRROR TOO: otherwise the next
    setUser(null);            //    session would inherit this identity.
    // ⚠️ AND WE LEAVE THE PRIVATE SCREEN (CCT-329). Flipping `user` to `null` switches
    // navigator, but the PUBLIC navigator reopens the remembered mission: the player was then
    // left on a half-public half-private card — empty banner, "Open the Trial" and "Buy" on a
    // mission they owned, controllers section gone. That is not a legitimate public page, it is
    // the private page with its data missing.
    try { sessionStorage.removeItem('ccjaw.missionDetail'); } catch { /* native: nothing to do */ }
  }, []);

  // SILENT RENEWAL: `restoreSession` only runs at start-up, so an access token (~1 h) expired
  // while the app stayed open -> 401 -> sign-out. We therefore renew periodically, and when the
  // tab regains focus -- the commonest case, since the app is left open and a background tab's
  // timers are throttled.
  useEffect(() => {
    if (!user) return;                       // no session: nothing to renew
    let stopped = false;
    const tick = async () => {
      if (stopped) return;
      try {
        const fresh = await entra.ensureFreshSession();
        if (fresh && !stopped) { applySession(fresh as any); return; }
        // ⚠️ A DEAD SESSION MUST NOT SURVIVE ON SCREEN — CCT-345. `null` means either "nothing
        // to do" (token still valid) or "the renewal failed", and both were treated as a
        // non-event. MEASURED on 2026-08-18: token expired 8 h 55 earlier with this tick every
        // 60 s, so roughly 540 silent failures, then a brutal sign-out on the user's first
        // gesture. The app showed a signed-in session all night while it had been dead from the
        // start.
        //
        // ⚠️ WE SIGN OUT ON A REFUSAL ONLY, NEVER ON AN OUTAGE. A server answering "no" says the
        // coupon is dead; an unreachable server says nothing, and throwing someone out on a
        // network hiccup or a restarting backend would be precisely the opposite defect. That is
        // why the verdict is explicit rather than inferred from a `null`.
        const expire = entra.currentSessionExpired?.() ?? false;
        if (!stopped && expire && entra.lastRefreshVerdict?.() === 'refuse') {
          deconnecter('renouvellement REFUSE sur un jeton expire');
        }
      } catch { /* best-effort: the 401 stays the arbiter */ }
    };
    void tick();                             // on mount (coming back to the app)
    const id = setInterval(tick, 60_000);
    // ⚠️ NOT `typeof window !== 'undefined'` — CCT-476. React Native defines `window`, so that
    // guard passed on a phone and `window.addEventListener` blew up one line later, killing every
    // native sign-in. `onForeground` answers the real question on both platforms.
    const unsubscribe = onForeground(() => { void tick(); });
    return () => {
      stopped = true;
      clearInterval(id);
      unsubscribe();
    };
    // STABLE DEPENDENCY (CCT-193): emphatically NOT `[user]`. The effect calls `applySession`,
    // which updates `user`; with the object as a dependency, every renewal re-armed the effect,
    // which triggered another renewal... a self-sustaining loop. The id only changes on a REAL
    // change of user (sign-in / sign-out).
  }, [user?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  // On-demand renewal, called by the API client on a 401 BEFORE signing out: the session is
  // only cleared when the refresh genuinely fails (token revoked/expired).
  useEffect(() => {
    // The client must be able to READ the current token: on a 401 that is how it tells
    // "the renewal failed" from "a concurrent call already renewed it" (CCT-127).
    setCurrentTokenReader(() => tokenRef.current ?? entra.currentAccessToken?.() ?? null);
    setRefreshHandler(async () => {
      try {
        // `forceRefresh` and NOT `ensureFreshSession(0)`: the latter answered "still valid"
        // (null) whenever the client believed its token good, even though the server had just
        // rejected it -- so we signed the user out WITHOUT attempting a renewal. Here `null`
        // means one thing only: the renewal failed.
        const fresh: any = await entra.forceRefresh();
        if (!fresh?.accessToken) { traceAuth('refresh-ko', tokenRef.current); return null; }
        traceAuth('refresh-ok', fresh.accessToken);
        applySession(fresh);
        return fresh.accessToken as string;
      } catch { return null; }
    });
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // Global 401 interceptor: expired/revoked token -> clear the session and remember the
  // current page; flipping user to null shows the PublicNavigator, which redirects to the
  // sign-in screen (returnTo).
  useEffect(() => {
    // Forgetting the open mission is enough to land back on the home screen; `returnTo` is kept
    // by `deconnecter`, so signing back in returns to where we were.
    setUnauthorizedHandler(() => { deconnecter(); });
  }, []);

  const login = async (signup = false, returnTo?: string): Promise<void> => {
    // returnTo is only passed when explicit: otherwise keep the one already remembered by the
    // guard / the 401 interceptor (do not overwrite it).
    // Web: redirects to Entra, so it never resolves. Native: resolves with the session.
    const s = (await entra.startLogin(signup, returnTo)) as any;
    await applySession(s);
  };

  const loginWithGoogle = async (): Promise<void> => {
    // DIRECT Google Sign-In (outside Entra): a Google id_token -> a backend session.
    // Web (GIS) and native (google-signin) both return a Session -> applySession.
    const s = await google.loginWithGoogle();
    await applySession(s);
  };

  const startEmailOtp = (email: string) => native.startEmailOtp(email);

  const startSignup = (email: string, attrs: SignupAttributes) => {
    // The attributes are captured AS SOON AS the form is submitted (before the OTP): the
    // reconciliation will persist them, even if the initial POST fails or the flow turns out
    // to be a sign-in.
    void setPendingProfile(attrs);
    return native.startSignup(email, attrs);
  };

  const submitOtp = async (code: string, profile?: SignupAttributes): Promise<void> => {
    // Sign-up: the profile is stored as PENDING; applySession() triggers the reconciliation
    // (POST /me/profile, replayed until confirmed). No more fire-and-forget POST whose failure
    // lost the data silently.
    if (profile) await setPendingProfile(profile);
    const s = await native.submitOtp(code);
    await applySession(s);
    // WEB: the OTP flow is IN-APP (no redirect), so the URL stays on `/Login`. But `Login` has
    // no path in linking.ts -> when switching to the MainNavigator, react-navigation cannot
    // route `/Login` -> a BLANK screen until a manual refresh. So we reload on the intended page
    // (returnTo, remembered by the guard/401) or on the home page, exactly like the Entra flow
    // (consumeReturnTo + window.location.replace): the bootstrap restores the session and mounts
    // a valid route (CCT-193 #1).
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const rt = entra.consumeReturnTo?.() || '/';
      window.location.replace(rt);
    }
  };

  const logout = () => {
    tokenRef.current = null;
    userRef.current = null;   // ⚠️ THE SYNCHRONOUS MIRROR TOO: otherwise the next
    setUser(null);            //    session would inherit this identity.
    clearInsightsUser(); // the anonymous session goes on, the identity is forgotten
    // The next account must RE-REGISTER the push subscription under its own name: on a shared
    // machine, leaving it under the outgoing user's name would send them the next user's
    // notifications.
    resetWebPushRegistration();
    // ⚠️ AND THE RUN STATE, FOR THE SAME REASON (CCT-291). The `/run` cache is keyed by
    // MISSION, not by user: on a shared machine the next account would read, within the
    // freshness window, the previous player's run -- their language, their progress. A cache
    // that outlives a change of identity is not a cache, it is a leak.
    oublierEtatPartie();
    // A NEW OBSERVABILITY SESSION (CCT-253). A session CARRIES an identity once it has one;
    // keeping it after sign-out would mix, in a single timeline, what the outgoing user did and
    // what the next one will do on the same machine. Wrong to read, and questionable to keep.
    // A sign-IN, on the other hand, cuts NOTHING: the activity that led to the login belongs to
    // the same story.
    try { newSessionId(); } catch { /* best-effort */ }
    // ⚠️ ASYNCHRONE DEPUIS CCT-320. La purge locale ne suffit plus : le coupon vit dans un
    // cookie HttpOnly que seul le SERVEUR peut effacer, sans quoi le retour sur l'accueil
    // reconnecte aussitôt. `native.logout()` appelle donc `/auth/session/logout` ET n'ordonne le
    // retour à l'accueil QU'APRÈS — l'enchaînement est tenu à l'intérieur, pas ici.
    void native.logout();
  };

  // STABLE REFERENCE (CCT-193): `getAccessToken` appears in screen effect dependencies (see
  // ChapterDetailScreen). Re-created on every render, it re-armed those effects on every render
  // of the provider -- the SAME mechanism as the renewal loop fixed by f4e04fe, moved one notch
  // along: the effect restarted the screen's bootstrap (scenario + progress + engine tick) and
  // put it back into "loading". The token lives in a `ref`, so this function has no dependency
  // and never needs a new identity.
  const getAccessToken = useCallback(() => tokenRef.current, []);

  return (
    <AuthContext.Provider value={{ user, isLoading, login, loginWithGoogle, startEmailOtp, startSignup, submitOtp, logout, getAccessToken, authNotice }}>
      {children}
    </AuthContext.Provider>
  );
};
